import React, {FC, useState, useEffect} from "react"
import * as PropTypes from "prop-types"
import {isNull} from "lodash"

import {FormattedMessage, FormattedCurrency} from "@irati/utils/lang"
import {withTheme, WithDefaultTheme} from "styled-components"
import {Row} from "react-bootstrap"
import {BulkRewardStatus, PaymentCurrencyType, WalletEntityType} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import {H2, H6, Label} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import {Input, ValidationButton} from "@irati/ui/src/FormItem"
import Validation from "@irati/utils/validation"
import RecipientList from "@irati-web/reward/src/components/Checkout/Steps/Recipients/RecipientList"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import WalletBallance from "@irati/wallet/apollo/query/Wallet"
import authentication from "@irati/auth"
import History from "@irati/utils/history"
import {Card} from "@irati/ui/src/Layout"
import Spacing from "@irati/ui/src/Spacing"
import {noDecimal} from "@irati/utils/validation/regex/number"
import {RouterLink} from "@irati/ui/src/Link"
import flexiPointService from "@irati/wallet/services/flexiRewards"
import ErrorText from "@irati/ui/src/ErrorText"
import {injectIntl, InjectIntlProps} from "@irati/utils/lang/injectIntl"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import FlexiRewardsErrorCodes from "@irati/wallet/errorCodes/FlexiRewards"
import getCodeFromError from "@irati/utils/services/API/helpers/getCodeFromError"
import {StepNames} from "../../../enums"
import {BulkRewardEvents} from "../../../enums/MixpanelEvents"
import {DistributeRewardImportType} from "../../../enums/DistributeRewardTypes"
import {DistributeRewardActions, DistributeRewardActionTypes, DistributeRewardState} from "../../../stores/DistributeReward/actions"

interface Props extends WithDefaultTheme, InjectIntlProps {
  payload: DistributeRewardState
  handlePrevStep: () => void
  dispatch: (action: DistributeRewardActionTypes) => void
}

const MIN_AMOUNT = 5

const distributeRewardPointsSchema = (pointsInWallet: number, numberOfUsers: number) => {
  return Validation.string("wallet.reward.admin.distribute.validation.pointsPerUser.required")
    .matches(noDecimal, "wallet.reward.admin.distribute.validation.integersOnly")
    .test(
      "Positive, value above 5 and no decimal",
      "wallet.reward.admin.distribute.validation.pointsPerUser.min",
      (val) => val >= MIN_AMOUNT || isNull(val)
    )
    .test(
      "Value cannot exceed wallet total",
      "wallet.reward.admin.distribute.validation.pointsPerUser.max",
      (val) => val * numberOfUsers <= pointsInWallet
    )
}

const startPolling: any = async (uuid: string) => {
  const {status} = await flexiPointService.bulkRewardStatus({
    uuid
  })
  if (status === BulkRewardStatus.AwaitingApproval) {
    return status
  }
  return new Promise((resolve, reject) => setTimeout(() => startPolling(uuid).then(resolve).catch(reject), 2000))
}
const DistributeRewardSummaryStep1: FC<Props> = ({theme, payload, handlePrevStep, dispatch, formatMessage}, context) => {
  const [showCSVError, setShowCSVError] = useState(false)
  const [submitting, setSubmitting] = useState<Promise<void>>()
  const [updatedPoints, setUpdatedPoints] = useState(false)
  const [networkError, setNetworkError] = useState<string | null>(null)
  useEffect(() => {
    if (!payload.bulkRewardState.csvError) {
      setShowCSVError(false)
    }
  }, [payload.bulkRewardState])
  const hasBulkRewardError = (status: BulkRewardStatus) => {
    if (status === BulkRewardStatus.Error || status === BulkRewardStatus.Unrecognized) {
      dispatch({
        type: DistributeRewardActions.SET_MODAL_ERROR_STATE,
        payload: {
          show: true,
          title: "wallet.adminReward.bulkRewards.errorModal.generic.title",
          content: "wallet.adminReward.bulkRewards.errorModal.generic.content",
          buttonText: "wallet.reward.admin.confirmationModal.btn.backToRewardWallet"
        }
      })
      return true
    }
    return false
  }

  const handlePointsPerUser = (pointsPerUser: string) => {
    if (payload.bulkRewardState.uuid && parseInt(pointsPerUser, 10) !== payload.pointsPerUser) {
      setUpdatedPoints(true)
    }
    dispatch({
      type: DistributeRewardActions.SET_USER_POINTS,
      payload: {
        userPoints: pointsPerUser
      }
    })
  }

  const nextStep = () => {
    const onNextStepPromise = new Promise<void>(async (resolve) => {
      if (
        payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV &&
        (location.pathname.includes(StepNames.Recipients) || updatedPoints)
      ) {
        if (payload.userEmails.length === 0) {
          setShowCSVError(true)
          return resolve()
        }
        setUpdatedPoints(false)
        // We create the BulkTransfer uuid
        try {
          const {uuid, status} = await flexiPointService.createBulkReward({
            sourceId: authentication.user().uuid!,
            amount: payload.pointsPerUser * 100,
            currency: PaymentCurrencyType.Rp,
            businessId: authentication.userTenant().uuid!,
            emails: payload.userEmails
          })

          if (hasBulkRewardError(status)) {
            return resolve()
          }
          if (status === BulkRewardStatus.Validating) {
            const pollingStatus = await startPolling(uuid)
            if (hasBulkRewardError(pollingStatus)) {
              return resolve()
            }
          }

          const failedEmails = await flexiPointService.bulkRewardFailedEmails({uuid})
          dispatch({
            type: DistributeRewardActions.SET_BULK_REWARD_STATE,
            payload: {
              uuid,
              failedEmails: failedEmails || []
            }
          })
          if (failedEmails.length) {
            tracking.uploadCsv(BulkRewardEvents.UserMatchError)
            return resolve()
          }
        } catch (err) {
          if (getCodeFromError(err) === FlexiRewardsErrorCodes.PBBulkRewardProcessing) {
            setNetworkError(getCodeFromError(err))
          }
          return resolve()
        }
      }

      if (location.pathname.includes(StepNames.Recipients)) {
        History.push(`/flexi-assign/${StepNames.Message}`, context)
      } else {
        History.push(`/flexi-assign/${StepNames.Confirm}`, context)
      }
      resolve()
    })
    setSubmitting(onNextStepPromise)
  }

  const totUsers =
    payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV ? payload.userEmails.length : payload.destinationUsers.length
  const totalToPay = payload.pointsPerUser * totUsers || 0

  return (
    <Card flush>
        <H6 paddingLeft={3} paddingTop={4}>
          <FormattedMessage id="wallet.reward.admin.summary.title" />
        </H6>
        <Flex direction="column" padding={3}>
          <Label large>
            <FormattedMessage id="wallet.reward.admin.distribute.enterPoints.title" />
          </Label>
          <WalletBallance
            variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
            {(apollo) => {
              return (
                <Row>
                  <Input
                    name="pointsPerUser"
                    type="number"
                    min="5"
                    value={String(payload.pointsPerUser)}
                    placeholder="wallet.reward.admin.distribute.enterPoints.placeholder"
                    validation={distributeRewardPointsSchema(Math.round(apollo.data.wallet.amount / 100), totUsers)}
                    onChange={handlePointsPerUser}
                  />
                </Row>
              )
            }}
          </WalletBallance>
        </Flex>

        {!!totUsers && (
          <Spacing paddingLeft={3} paddingRight={3} marginBottom={2}>
            <Label large>
              <FormattedMessage id="wallet.reward.admin.distribute.recipients" />
            </Label>
            {payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV && (
              <Label large marginLeft marginBottom display="block" marginTop={2}>
                <FormattedMessage id="wallet.reward.admin.distribute.recipientsCount" values={{total: totUsers}} />
              </Label>
            )}
            {payload.rewardImportType === DistributeRewardImportType.MANUAL_ENTER && (
              <RecipientList recipients={payload.destinationUsers} />
            )}
          </Spacing>
        )}

        <Flex style={{background: theme.colors.utility.highlight}} justifyContent="center" paddingTop={2} paddingBottom={2}>
          <Flex direction="column" alignItems="center">
            <Label small marginBottom={0.5}>
              <FormattedMessage id="wallet.reward.intro.available" />
            </Label>
            <WalletBallance
              variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
              {(apollo) => {
                return (
                  <>
                    <FormattedCurrency value={apollo.data.wallet.amount} currency={PaymentCurrencyType.Rp} />
                    {!!authentication.apps("wallet.flexiRewards.canTopUpRewardPoints") && totalToPay > apollo.data.wallet.amount / 100 && (
                      <RouterLink to="/flexi-checkout/top-up" marginTop>
                        <FormattedMessage id="wallet.reward.intro.topup" />
                      </RouterLink>
                    )}
                  </>
                )
              }}
            </WalletBallance>
          </Flex>
        </Flex>

        <Flex justifyContent="space-between" alignItems="center" padding={3}>
          <Label large>
            <FormattedMessage id="wallet.reward.admin.card.topup.totalToPay" />
          </Label>
          <Flex alignItems="center">
            <Icon icon={IconTypes.rewardPoints} size={32} />
            <WalletBallance
              variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
              {(apollo) =>
                totalToPay > apollo.data.wallet.amount / 100 ? (
                  <H2 marginLeft={1} color={theme.colors.tertiary.r_400}>
                    {totalToPay}
                  </H2>
                ) : (
                  <H2 marginLeft={1}>{totalToPay}</H2>
                )
              }
            </WalletBallance>
          </Flex>
        </Flex>

        <Flex justifyContent="space-between" paddingLeft={3} paddingRight={3} paddingBottom={3}>
          {!location.pathname.includes(StepNames.Recipients) && (
            <Button id="rewardWallet_distributeRewardStep1_back_button" onClick={handlePrevStep} marginRight>
              <FormattedMessage id="platform.button.back" />
            </Button>
          )}
          <ValidationButton
            id="rewardWallet_distributeReward_continue_button"
            full
            primary
            onClick={nextStep}
            load={submitting}
            loading={formatMessage({id: "platform.button.validating"})}>
            <FormattedMessage id="platform.button.continue" />
          </ValidationButton>
        </Flex>
        {networkError && (
          <Flex justifyContent="center" paddingBottom={2}>
            <ErrorText>
              <FormattedMessage id="errors.PBFlexiPointsAppExistingBulkRewardProcessing" />
            </ErrorText>
          </Flex>
        )}
        {showCSVError && (
          <Flex justifyContent="center" paddingBottom={2}>
            <ErrorText>
              <FormattedMessage
                id={
                  payload.bulkRewardState.csvError
                    ? "wallet.adminReward.bulkRewards.errors.csvHasErrors"
                    : "wallet.adminReward.bulkRewards.errors.incorrectCSV"
                }
              />
            </ErrorText>
          </Flex>
        )}
      </Card>
  )
}

DistributeRewardSummaryStep1.contextTypes = {
  appPath: PropTypes.string.isRequired
}

export default injectIntl(withTheme(DistributeRewardSummaryStep1))
